import React, { lazy } from 'react';
import lazyRetry from 'common/helpers/componentLoader';
import ActivityTabHeader from '../components/ActivityTabHeader';

export const Routes = {
  createNewSensors: '/devices/new',
  admin: '/admin-dashboard',
  home: '/',
  support: 'https://petasense.freshdesk.com/support/home'
};

// Memoize lazy components - create them ONCE, not on every render
const DashboardComponent = lazy(() => lazyRetry(() => import('../../Dashboard')));
const ActivityComponent = lazy(() => lazyRetry(() => import('../../Activity/Activity')));
const AssetsComponent = lazy(() => lazyRetry(() => import('../../Machines/components/MachinesPage')));
const TemplatesComponent = lazy(() => lazyRetry(() => import('../../Templates/components/pages/Templates')));
const DevicesComponent = lazy(() => lazyRetry(() => import('../../Sensors/components/Sensors')));
const EventDefinitionsComponent = lazy(() => lazyRetry(() => import('../../Settings/EventDefinitions/DefinitionPage')));
const DiagnosticRulesComponent = lazy(() => lazyRetry(() => import('../../MachineDiagnostics/RulesPage')));
const SensorModelsComponent = lazy(() => lazyRetry(() => import('../../Settings/SensorModels/components/SensorModelPage')));
const AutomaticAlarmsComponent = lazy(() => lazyRetry(() => import('home/AutomaticAlarms/components/pages/AutomaticAlarm')));
const ReportsComponent = lazy(() => lazyRetry(() => import('../../Reports/components/Reports')));
const InstrumentationAlertsComponent = lazy(() => lazyRetry(() => import('../../InstrumentationAlerts/InstrumentationAlerts')));

/**
 * homeTabItems now needs i18n `t` passed in.
 * Usage (inside a component):
 *   const { t } = useTranslation();
 *   const tabs = getHomeTabItems(t);
 *
 * `t` defaults to an identity function so non-i18n callers
 * (e.g. constructor-time use that only reads `.key`) do not crash.
 */
export const getHomeTabItems = (t = key => key) => [
  {
    path: '/',
    key: 'overview',
    title: t('home.tabs.dashboards'),
    component: DashboardComponent
  },
  {
    path: '/activity',
    key: 'activity',
    title: (
      <ActivityTabHeader />
    ),
    component: ActivityComponent
  },
  {
    path: '/assets',
    key: 'assets',
    title: t('home.tabs.assets'),
    component: AssetsComponent
  },
  {
    path: '/templates',
    key: 'templates',
    title: t('home.tabs.assetTemplates'),
    component: TemplatesComponent
  },
  {
    path: '/devices',
    key: 'devices',
    title: t('home.tabs.devices'),
    component: DevicesComponent
  },
  {
    path: '/event-definitions',
    key: 'event-definitions',
    title: t('home.tabs.eventDefinitions'),
    component: EventDefinitionsComponent
  },
  {
    path: '/diagnostic-rules',
    key: 'diagnostic-rules',
    title: t('home.tabs.diagnosticRules'),
    component: DiagnosticRulesComponent
  },
  {
    path: '/sensor-models',
    key: 'sensor-models',
    title: t('home.tabs.sensorModels'),
    component: SensorModelsComponent
  },
  {
    path: '/automatic-alarms',
    key: 'automatic-alarms',
    title: t('home.tabs.automaticAlarms'),
    component: AutomaticAlarmsComponent
  },
  {
    path: '/reports',
    key: 'reports',
    title: t('home.tabs.reports'),
    component: ReportsComponent
  },
  {
    path: '/instrumentation-alerts',
    key: 'instrumentation-alerts',
    title: t('home.tabs.instrumentationAlerts'),
    component: InstrumentationAlertsComponent
  }
];

export const tabsOnlyForPetasenseAdmins = [
  'feeds',
  'alarm-limits'
];

// Staff tools: Petasense admins, and partner admins for the accounts they manage.
export const tabsForPetasenseAndPartnerAdmins = [
  'event-replay'
];

export const tabsOnlyForAnalystsAndAdmins = [
  'audit-logs'
];

export const tabsOnlyForAnalysts = [
  'teams',
  'fmea-templates'
];

export const groupsExcludedForPartners = [
  'help'
];

export const tabsOnlyForOrgAdminsAndPetasenseAdmins = [
  'organisation'
];

// Top-level route paths that render full-width, with the asset-hierarchy side
// pane hidden. Add a path here to opt a flow out of the hierarchy pane.
export const pathsWithoutAssetHierarchy = [
  'fmea-templates'
];

// Memoize lazy components for account menu
const SiteProfileComponent = lazy(() => lazyRetry(() => import('../../Settings/SiteProfile')));
const AccountUsersComponent = lazy(() => lazyRetry(() => import('../../Settings/AccountUsers/AccountUsers')));
const TeamsPageComponent = lazy(() => lazyRetry(() => import('../../Settings/Teams/components/TeamsPage')));
const TermsPageComponent = lazy(() => lazyRetry(() => import('../../../onboarding/components/TermsPage')));
const ThirdPartyTagsComponent = lazy(() => lazyRetry(() => import('../../Settings/ThirdPartyTags/ThirdPartyTags')));
const ThirdPartyIntegrationsComponent = lazy(() => lazyRetry(() => import('../../Settings/ThirdPartyIntegrations/ThirdPartyIntegrations')));
const TagTemplateComponent = lazy(() => lazyRetry(() => import('../../TagTemplates/TagTemplate')));
const TaskTemplatesComponent = lazy(() => lazyRetry(() => import('../../TaskTemplates/TaskTemplates')));
const FmeaTemplatesComponent = lazy(() => lazyRetry(() => import('../../FmeaTemplates/FmeaTemplates')));
const APIAuditLogsComponent = lazy(() => lazyRetry(() => import('../../APIAuditLogs/APIAuditLogs')));
const DevicesHealthComponent = lazy(() => lazyRetry(() => import('../../DevicesHealth/DevicesHealth')));
const DownloadLimitsComponent = lazy(() => lazyRetry(() => import('../../Settings/DownloadLimits')));
const OrganisationSettingsComponent = lazy(() => lazyRetry(() => import('../../Settings/OrganisationSettings/OrganisationSettings')));
const EventReplayComponent = lazy(() => lazyRetry(() => import('../../EventReplay/EventReplay')));

/**
 * Account menu items also need i18n `t`.
 * Callers that don't have translation context can call
 * `getAccountMenuItems()` and will get raw keys as display names.
 */
export const getAccountMenuItems = (t = key => key) => [
  {
    key: 'settings',
    displayName: t('home.accountMenu.settings.title'),
    items: [
      {
        key: 'organisation',
        displayName: t('home.accountMenu.settings.organisation'),
        path: '/organisation',
        component: OrganisationSettingsComponent
      },
      {
        key: 'site-profile',
        displayName: t('home.accountMenu.settings.site'),
        path: '/site-profile',
        component: SiteProfileComponent
      },
      {
        key: 'users',
        displayName: t('home.accountMenu.settings.users'),
        path: '/users',
        component: AccountUsersComponent
      },
      {
        key: 'teams',
        displayName: t('home.accountMenu.settings.teams'),
        path: '/teams',
        component: TeamsPageComponent
      },
      {
        key: 'saas-agreement',
        displayName: t('home.accountMenu.settings.saasAgreement'),
        path: '/saas-agreement',
        component: TermsPageComponent
      },
      {
        key: 'third-party-tags',
        displayName: t('home.accountMenu.settings.thirdPartyTags'),
        path: '/third-party-tags',
        component: ThirdPartyTagsComponent
      },
      {
        key: 'third-party-integrations',
        displayName: t('home.accountMenu.settings.thirdPartyIntegrations'),
        path: '/third-party-integrations',
        component: ThirdPartyIntegrationsComponent
      },
      {
        key: 'tag-templates',
        displayName: t('home.accountMenu.settings.tagTemplates'),
        path: '/tag-templates',
        component: TagTemplateComponent
      },
      {
        key: 'task-templates',
        displayName: t('home.accountMenu.settings.taskTemplates'),
        path: '/task-templates',
        component: TaskTemplatesComponent
      },
      {
        key: 'fmea-templates',
        displayName: t('home.accountMenu.settings.fmeaTemplates'),
        path: '/fmea-templates',
        component: FmeaTemplatesComponent
      },
      {
        key: 'audit-logs',
        displayName: t('home.accountMenu.settings.auditLogs'),
        path: '/audit-logs',
        component: APIAuditLogsComponent
      },
      {
        key: 'event-replay',
        displayName: t('home.accountMenu.settings.eventReplay'),
        path: '/event-replay',
        component: EventReplayComponent
      },
      {
        // No component: ProtectedLayout renders release notes as a standalone
        // page outside the app shell, so this entry is only the menu link.
        key: 'release-notes',
        displayName: t('home.accountMenu.settings.releaseNotes'),
        path: '/release-notes'
      },
    ]
  },
  {
    key: 'help',
    displayName: t('home.accountMenu.help.title'),
    items: [
      {
        key: 'customer-support-portal',
        displayName: t('home.accountMenu.help.support'),
        path: 'https://petasense.freshdesk.com/support/login',
        external: true
      }
    ]
  },
  {
    key: 'others',
    displayName: t('home.accountMenu.others.title'),
    items: [
      {
        key: 'devices-health',
        displayName: t('home.accountMenu.others.devicesHealth'),
        path: '/devices-health',
        component: DevicesHealthComponent
      },
      {
        key: 'alarm-limits',
        displayName: t('home.accountMenu.others.alarmLimits'),
        path: '/alarm-limits',
        component: DownloadLimitsComponent,
      }
    ]
  }
];
